import { useEffect, useRef } from 'react';
import { LIVE_INTERVAL_MS } from './prefs';

/**
 * Re-read the page on a timer, and NOT while nobody is looking.
 *
 * The pause is what makes this affordable rather than a courtesy: every tick
 * is a batch of reads against dispatch, and a dashboard left open on a
 * background tab overnight would otherwise spend the night asking. The
 * dashboard's own controller pauses the same way — this is that rule, copied.
 *
 * Coming back to the tab reloads at once rather than on the next tick: the
 * figures on screen are as old as the tab has been hidden.
 *
 * `reload` is usually rebuilt on every render, so the timer reads it out of a
 * ref instead of depending on it; depending on it would clear and re-arm the
 * interval on each render and the tick would never arrive.
 */
export function useLiveRefresh(enabled: boolean, reload: () => void, intervalMs: number = LIVE_INTERVAL_MS): void {
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;

    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer);
      timer = undefined;
    };
    const start = () => {
      stop();
      timer = window.setInterval(() => reloadRef.current(), intervalMs);
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else {
        reloadRef.current();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs]);
}
